import { GroupDataFromSearchResult } from '../../../../interfaces/group.interface';
import { GroupParticipationReportRow } from '../../../../interfaces/insights.interface';
import { AppLocale } from '../../../../i18n/config';
import { getMonthName } from '../../../../i18n/analytics';

const escapeDelimitedValue = (value: string, delimiter: string) => {
  const escapedValue = value.replace(/"/g, '""');
  const shouldQuote = value.includes(delimiter) || /["\n\r]/.test(value);

  return shouldQuote ? `"${escapedValue}"` : escapedValue;
};

const getResponsibleUsersLabel = (group?: GroupDataFromSearchResult) => {
  if (!group?.responsible_users?.length) {
    return '';
  }

  return group.responsible_users
    .map((responsibleUser) => {
      const fullName = [responsibleUser.first_name, responsibleUser.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();

      return fullName || responsibleUser.email || '';
    })
    .filter(Boolean)
    .join(', ');
};

const formatAdherence = (value: number | null, locale: string) => {
  if (value === null || Number.isNaN(value)) {
    return '';
  }

  return `${value.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

type ExportGroupParticipationCsvParams = {
  rows: GroupParticipationReportRow[];
  groups: GroupDataFromSearchResult[];
  locale: AppLocale;
  companyName?: string;
  periodMonth?: number;
  periodYear?: number;
  headers: {
    responsibleUsers: string;
    grouping: string;
    groupAdherence: string;
  };
  fileNamePrefix: string;
};

const sanitizeCompanyNameForFile = (companyName?: string) => {
  const normalized = (companyName || 'empresa')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9À-ÿ-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'empresa';
};

const sanitizeFileNamePart = (value: string) => value
  .trim()
  .replace(/\s+/g, '-')
  .replace(/[^a-zA-Z0-9À-ÿ-]/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const formatExportDateTime = (date: Date, locale: AppLocale) => {
  const datePart = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date).replace(/[^0-9]+/g, '-');
  const timePart = date
    .toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(/[^0-9]+/g, '-');

  return `${datePart}--${timePart}`;
};

const formatReportPeriod = (month?: number, year?: number, locale?: AppLocale) => {
  if (!month || !year || !locale) {
    return '';
  }

  const monthLabel = getMonthName(month, locale, true);
  const formattedMonthLabel = monthLabel
    ? `${monthLabel.charAt(0).toLocaleUpperCase(locale)}${monthLabel.slice(1)}`
    : '';

  return formattedMonthLabel ? `${formattedMonthLabel}/${year}` : '';
};

const buildGroupParticipationRows = ({
  rows,
  groups,
  locale,
  periodMonth,
  periodYear,
  headers,
}: Pick<ExportGroupParticipationCsvParams, 'rows' | 'groups' | 'locale' | 'periodMonth' | 'periodYear' | 'headers'>) => {
  const groupsById = new Map(groups.map((group) => [String(group.id), group]));
  const periodLabel = formatReportPeriod(periodMonth, periodYear, locale);
  const groupAdherenceHeader = periodLabel ? `${headers.groupAdherence} - ${periodLabel}` : headers.groupAdherence;
  const dataRows = rows.map((row) => [
    getResponsibleUsersLabel(groupsById.get(String(row.groupId))),
    row.groupName,
    formatAdherence(row.lastMonthAdherence, locale),
  ]);

  return [[headers.responsibleUsers, headers.grouping, groupAdherenceHeader], ...dataRows];
};

const buildDelimitedContent = (rows: string[][], delimiter: string) =>
  rows
    .map((row) => row.map((value) => escapeDelimitedValue(value, delimiter)).join(delimiter))
    .join('\n');

export const exportGroupParticipationCsv = ({
  rows,
  groups,
  locale,
  companyName,
  periodMonth,
  periodYear,
  headers,
  fileNamePrefix,
}: ExportGroupParticipationCsvParams) => {
  const exportRows = buildGroupParticipationRows({ rows, groups, locale, periodMonth, periodYear, headers });
  const csvContent = `\uFEFF${buildDelimitedContent(exportRows, ';')}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const reportPeriod = formatReportPeriod(periodMonth, periodYear, locale);
  const reportPeriodFilePart = reportPeriod ? sanitizeFileNamePart(reportPeriod) : '';
  const fileName = [
    sanitizeFileNamePart(fileNamePrefix),
    sanitizeCompanyNameForFile(companyName),
    reportPeriodFilePart,
    formatExportDateTime(new Date(), locale),
  ].filter(Boolean).join('---');

  link.href = url;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const copyTextWithFallback = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Utiliza o fallback abaixo quando a permissão da área de transferência é negada.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();

  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error('Não foi possível copiar os dados para a área de transferência.');
  }
};

export const copyGroupParticipationToClipboard = async ({
  rows,
  groups,
  locale,
  periodMonth,
  periodYear,
  headers,
}: Pick<ExportGroupParticipationCsvParams, 'rows' | 'groups' | 'locale' | 'periodMonth' | 'periodYear' | 'headers'>) => {
  const exportRows = buildGroupParticipationRows({ rows, groups, locale, periodMonth, periodYear, headers });
  await copyTextWithFallback(buildDelimitedContent(exportRows, '\t'));
};
